import React, { useEffect } from 'react';
import useUserPermission from '../ai2/hooks/useUserPermission';
import { SystemPermission } from '../context';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useDashboardTopBar } from '../ai2/hooks';
import { useRoutes } from '../context/RoutesProvider';
import { isEmpty } from 'lodash';

type MenuItem = {
  id: string;
  url?: string;
  type: 'link' | 'placeholder' | 'hamburgerMenu';
  order: number;
  needPermission?: any;
  options?: MenuItem[];
};

interface Props {
  isAuth: boolean;
}
const IndexRoute = ({ isAuth }: Props) => {
  const { userHasPermissions } = useUserPermission();
  const { urlExists } = useRoutes();
  const { menuItems } = useDashboardTopBar();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const { userSavedFilters, savedFiltersInitialized } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );

  const navigate = useNavigate();

  const navigateToFirstValidItem = (menuItems: MenuItem[]) => {
    if (selectedTenancy && !selectedTenancy?.permissionsSetup) {
      navigate('/dashboard');
      return;
    }
    const sortedMenu = [...menuItems].sort((a, b) => a.order - b.order);
    const hasPermission = (item: MenuItem): boolean => {
      if (!item.needPermission) return true;

      const entry = Object.entries(item.needPermission)[0] as [
        'or' | 'and',
        SystemPermission | SystemPermission[],
      ];
      if (!entry) return true;

      const [mode, permissions] = entry;
      if (!mode || !permissions) return true;

      return userHasPermissions(permissions, mode);
    };

    for (const item of sortedMenu) {
      if (item.url && !urlExists(item.url)) continue;
      if (item.type === 'link' && hasPermission(item)) {
        navigate(item.url!);
        return;
      }

      if (item.type === 'hamburgerMenu' && item.options) {
        for (const subItem of item.options) {
          if (subItem.type === 'link' && hasPermission(subItem)) {
            navigate(subItem.url!);
            return;
          }
        }
      }
    }
    navigate('/');
  };

  useEffect(() => {
    if (!selectedTenancy || !isAuth) {
      return;
    }

    const hasSearchFeature = !isEmpty(
      selectedTenancy?.features?.searchFiltersMap,
    );

    if (hasSearchFeature && !savedFiltersInitialized) {
      return;
    }

    // Prefer explicit redirect rules for dashboard/search based on pinned filters
    const canViewDashboard =
      selectedTenancy?.permissionsSetup &&
      userHasPermissions(SystemPermission.VIEW_PATIENT_LIST);

    const userPinnedFilter = (
      userSavedFilters.find((item) => item.pinned)?.text || ''
    )
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);

    if (canViewDashboard) {
      // If search feature exists but user has no pinned filter, land on /search
      if (hasSearchFeature && userPinnedFilter.length === 0) {
        navigate('/search');
        return;
      }
      // Otherwise go to /dashboard
      navigate('/dashboard');
      return;
    }

    // Fallback to first valid menu item
    if (menuItems && menuItems.length > 0) {
      navigateToFirstValidItem(menuItems as any);
      return;
    }
    navigate('/dashboard');
  }, [
    selectedTenancy,
    isAuth,
    menuItems,
    userSavedFilters,
    savedFiltersInitialized,
  ]);

  return <></>;
};

export default IndexRoute;
